// @djsem/live-core — Rehearsal Mode Foundation (Prompt 12B).
//
// Deterministic rehearsal driven by an APPROVED SNAPSHOT only (rules 5, 6, 11: mutable
// draft package/set are rejected). Simulates track progression, activates chapters/cues
// by time, produces DJSEMOutputFrame, respects safe mode / blackout (rule 9), and logs
// every event. No hardware output; no live protocol here.

import type { DJSEMLiveEventEnvelope } from "./protocol";
import { SeqCounter } from "@djsem/shared";
import type { LivePlaybackState } from "./types";
import type { DJSEMOutputFrame } from "@djsem/render-state";
import { type DJSEMValidationResult, toDJSEMValidationResult, type ValidationResult } from "@djsem/shared";

// ---- contracts --------------------------------------------------------------

/** A rehearsal run's current state, driven deterministically from an approved snapshot. */
export interface DJSEMRehearsalSession {
  sessionId: string;
  snapshotId: string;
  snapshotKind: "approved_package_snapshot" | "approved_set_snapshot";
  status: "idle" | "running" | "paused" | "completed" | "safe" | "blackout" | "failed";
  startedAt?: string;
  endedAt?: string;
  currentTimeMs: number;
  currentTrackId?: string;
  currentChapterId?: string;
  currentCueId?: string;
  warnings: string[];
}

/** An operator command applied to a {@link DJSEMRehearsalSession} via {@link RehearsalModeService.applyCommand}. */
export interface DJSEMRehearsalCommand {
  commandId: string;
  commandType:
    | "start" | "pause" | "resume" | "seek" | "trigger_manual_cue"
    | "enable_safe_mode" | "disable_safe_mode" | "blackout" | "clear_blackout" | "stop";
  payload?: unknown;
  createdAt: string;
}

/** One produced output-frame tick of a rehearsal session, from {@link RehearsalModeService.tick}. */
export interface DJSEMRehearsalFrame {
  frameId: string;
  sessionId: string;
  createdAt: string;
  playbackState: LivePlaybackState;
  outputFrame: DJSEMOutputFrame;
  activeSnapshotId: string;
  warnings: string[];
}

/** A full recorded rehearsal run (commands, events, frames), exported by {@link RehearsalModeService.exportReplay}. */
export interface DJSEMReplayLog {
  sessionId: string;
  snapshotId: string;
  createdAt: string;
  commands: DJSEMRehearsalCommand[];
  events: DJSEMLiveEventEnvelope[];
  frames: DJSEMRehearsalFrame[];
}

// Snapshot shapes we consume (structurally typed; we don't import the analysis-contract
// class to avoid a runtime dep — a facade lives in the docs/tests).
interface ApprovedPackageSnapshotLike {
  snapshotId: string; snapshotKind: "approved_package_snapshot"; snapshotHash: string;
  finalPackage: Record<string, unknown>; preparationTrackId: string;
}
interface ApprovedSetSnapshotLike {
  snapshotId: string; snapshotKind: "approved_set_snapshot"; snapshotHash: string;
  finalSet: { id: string; tracks: Array<{ preparationTrackId: string; assignedChapterId?: string }>; chapters: Array<{ id: string; startTimeMs?: number; endTimeMs?: number; startBar?: number; endBar?: number }> };
  approvedTrackPackageRefs: Array<{ preparationTrackId: string; approvedPackageSnapshotId: string; approvedPackageSnapshotHash: string }>;
}
type ApprovedSnapshotLike = ApprovedPackageSnapshotLike | ApprovedSetSnapshotLike;

// ---- inputs -----------------------------------------------------------------

/** Input for {@link RehearsalModeService.createSession}. */
export interface CreateRehearsalSessionInput { snapshot: ApprovedSnapshotLike; sessionId?: string; startedAt?: string; }
/** Input for {@link RehearsalModeService.applyCommand}. */
export interface ApplyRehearsalCommandInput { session: DJSEMRehearsalSession; command: DJSEMRehearsalCommand; snapshot: ApprovedSnapshotLike; }
/** Input for {@link RehearsalModeService.tick}. */
export interface RehearsalTickInput { session: DJSEMRehearsalSession; snapshot: ApprovedSnapshotLike; deltaMs: number; createdAt?: string; }
/** Input for {@link RehearsalModeService.loadApprovedSnapshot}. */
export interface LoadApprovedSnapshotInput { snapshot: unknown; }
/** Result of {@link RehearsalModeService.loadApprovedSnapshot}. */
export interface LoadedRehearsalSnapshot { ok: boolean; snapshot?: ApprovedSnapshotLike; error?: string; }
/** Input for {@link RehearsalModeService.generateSimulatedEvents}. */
export interface GenerateSimulatedEventsInput { session: DJSEMRehearsalSession; snapshot: ApprovedSnapshotLike; }
/** Input for {@link RehearsalModeService.exportReplay}. */
export interface ExportRehearsalReplayInput { session: DJSEMRehearsalSession; commands: DJSEMRehearsalCommand[]; events: DJSEMLiveEventEnvelope[]; frames: DJSEMRehearsalFrame[]; }

const AT = "1970-01-01T00:00:00.000Z";

function safeFrame(now: number, reason: string, pkgId?: string): DJSEMOutputFrame {
  return {
    timestamp: now, packageId: pkgId,
    ledWall: { mode: "safe_hold", palette: [], intensity: 0, motion: "none", safe: true },
    lasers: { mode: "safe_hold", intensity: 0, triggers: [], safe: true },
    projectors: { mode: "safe_hold", emojiSequence: [], density: 0, intensity: 0, safe: true },
    intensityBudget: { total: 0, ledWall: 0, lasers: 0, projectors: 0, dominantLayer: "safe" },
    safety: { safeModeActive: true, fallbackVisualMode: "safe_hold", reason },
  };
}
function blackoutFrame(now: number, pkgId?: string): DJSEMOutputFrame {
  return {
    timestamp: now, packageId: pkgId,
    ledWall: { mode: "blackout", palette: [], intensity: 0, motion: "none", safe: true },
    lasers: { mode: "blackout", intensity: 0, triggers: [], safe: true },
    projectors: { mode: "blackout", emojiSequence: [], density: 0, intensity: 0, safe: true },
    intensityBudget: { total: 0, ledWall: 0, lasers: 0, projectors: 0, dominantLayer: "blackout" },
    safety: { safeModeActive: true, fallbackVisualMode: "blackout", reason: "blackout" },
  };
}
function activeFrame(now: number, pkgId: string | undefined, cueId: string | undefined, intensity: number): DJSEMOutputFrame {
  const cl = Math.max(0, Math.min(1, intensity));
  return {
    timestamp: now, packageId: pkgId, cueId,
    ledWall: { mode: "active", palette: [], intensity: cl, motion: "medium", safe: false },
    lasers: { mode: "active", intensity: cl * 0.6, triggers: [], safe: false },
    projectors: { mode: "active", emojiSequence: [], density: cl * 0.8, intensity: cl * 0.8, safe: false },
    intensityBudget: { total: cl, ledWall: cl, lasers: cl * 0.6, projectors: cl * 0.8, dominantLayer: "ledWall" },
    safety: { safeModeActive: false, fallbackVisualMode: undefined, reason: undefined },
  };
}

function trackListFrom(snap: ApprovedSnapshotLike): Array<{ preparationTrackId: string }> {
  return snap.snapshotKind === "approved_set_snapshot"
    ? snap.finalSet.tracks.map((t) => ({ preparationTrackId: t.preparationTrackId }))
    : [{ preparationTrackId: snap.preparationTrackId }];
}
function chaptersFrom(snap: ApprovedSnapshotLike): Array<{ id: string; startTimeMs?: number; endTimeMs?: number }> {
  return snap.snapshotKind === "approved_set_snapshot" ? snap.finalSet.chapters : [];
}

// ---- service (class-based; hard rule 12) ------------------------------------

const _seq = new SeqCounter();
const nextId = (prefix: string) => `${prefix}_${_seq.next()}`;

/**
 * Deterministic rehearsal driven only by an APPROVED snapshot (mutable draft package/set
 * inputs are rejected). Simulates track progression, activates chapters/cues by time,
 * produces output frames, respects safe mode / blackout, and logs every event. No hardware
 * output; no live protocol involved.
 */
export class RehearsalModeService {
  /**
   * Validates that a candidate snapshot is an approved package/set snapshot usable for rehearsal.
   * @param input - the candidate snapshot of unknown shape.
   * @returns `{ ok: true, snapshot }` if valid, otherwise `{ ok: false, error }`.
   */
  loadApprovedSnapshot(input: LoadApprovedSnapshotInput): LoadedRehearsalSnapshot {
    const s = input.snapshot as ApprovedSnapshotLike;
    if (!s || typeof s !== "object") return { ok: false, error: "not an object" };
    if (s.snapshotKind !== "approved_package_snapshot" && s.snapshotKind !== "approved_set_snapshot")
      return { ok: false, error: "only approved snapshots can drive rehearsal" };
    if (!s.snapshotId || !s.snapshotHash) return { ok: false, error: "missing snapshot id or hash" };
    return { ok: true, snapshot: s };
  }

  /**
   * Creates a new idle rehearsal session from an approved snapshot.
   * @param input - the approved snapshot and optional session id/start timestamp.
   * @returns the new idle session.
   * @throws if the snapshot is not an approved package/set snapshot.
   */
  createSession(input: CreateRehearsalSessionInput): DJSEMRehearsalSession {
    const load = this.loadApprovedSnapshot({ snapshot: input.snapshot });
    if (!load.ok) throw new Error(`Cannot create rehearsal session: ${load.error}`);
    const tracks = trackListFrom(input.snapshot);
    return {
      sessionId: input.sessionId ?? nextId("rh"),
      snapshotId: input.snapshot.snapshotId,
      snapshotKind: input.snapshot.snapshotKind,
      status: "idle",
      startedAt: input.startedAt,
      currentTimeMs: 0,
      currentTrackId: tracks[0]?.preparationTrackId,
      warnings: [],
    };
  }

  /**
   * Applies an operator command (start/pause/resume/seek/blackout/safe-mode/manual cue/etc.)
   * to a rehearsal session.
   * @param input - the current session, the command, and the driving snapshot.
   * @returns the updated session.
   */
  applyCommand(input: ApplyRehearsalCommandInput): DJSEMRehearsalSession {
    const s = { ...input.session };
    switch (input.command.commandType) {
      case "start": s.status = "running"; s.startedAt = s.startedAt ?? input.command.createdAt; break;
      case "pause": if (s.status === "running") s.status = "paused"; break;
      case "resume": if (s.status === "paused") s.status = "running"; break;
      case "stop": s.status = "completed"; s.endedAt = input.command.createdAt; break;
      case "seek": {
        const to = Math.max(0, Number((input.command.payload as { timeMs?: number })?.timeMs ?? 0));
        s.currentTimeMs = to;
        s.currentChapterId = this.chapterAt(input.snapshot, to)?.id;
        break;
      }
      case "trigger_manual_cue": {
        const cueId = String((input.command.payload as { cueId?: string })?.cueId ?? "manual_cue");
        s.currentCueId = cueId;
        s.warnings = [...s.warnings, `Manual cue triggered: ${cueId}`];
        break;
      }
      case "enable_safe_mode": s.status = "safe"; break;
      case "disable_safe_mode": if (s.status === "safe") s.status = "running"; break;
      case "blackout": s.status = "blackout"; break;
      case "clear_blackout": if (s.status === "blackout") s.status = "running"; break;
    }
    return s;
  }

  /**
   * Advances a running rehearsal session by `deltaMs` and produces the resulting frame.
   * @param input - the current session, driving snapshot, elapsed time, and optional timestamp.
   * @returns the produced rehearsal frame (playback state + output frame).
   */
  tick(input: RehearsalTickInput): DJSEMRehearsalFrame {
    const at = input.createdAt ?? AT;
    let session = input.session;
    if (session.status === "running") {
      const nextTime = Math.max(0, session.currentTimeMs + Math.max(0, input.deltaMs));
      const chapter = this.chapterAt(input.snapshot, nextTime);
      session = { ...session, currentTimeMs: nextTime, currentChapterId: chapter?.id ?? session.currentChapterId };
    }
    const pkgId = input.snapshot.snapshotKind === "approved_package_snapshot" ? input.snapshot.snapshotId : input.snapshot.finalSet.id;
    let frame: DJSEMOutputFrame;
    if (session.status === "blackout") frame = blackoutFrame(session.currentTimeMs, pkgId);
    else if (session.status === "safe") frame = safeFrame(session.currentTimeMs, "safe mode", pkgId);
    else if (session.status === "running") frame = activeFrame(session.currentTimeMs, pkgId, session.currentCueId, 0.6);
    else frame = safeFrame(session.currentTimeMs, `idle (${session.status})`, pkgId);
    const playback: LivePlaybackState = {
      packageId: pkgId,
      trackId: session.currentTrackId,
      positionMs: session.currentTimeMs,
      isPlaying: session.status === "running",
      lastEventAt: at,
    } as unknown as LivePlaybackState;
    return {
      frameId: `f_${session.sessionId}_${session.currentTimeMs}`,
      sessionId: session.sessionId,
      createdAt: at,
      playbackState: playback,
      outputFrame: frame,
      activeSnapshotId: session.snapshotId,
      warnings: session.warnings,
    };
  }

  /**
   * Deterministically generates one "chapter.enter" simulated event per chapter with a
   * defined start time.
   * @param input - the session and its driving snapshot.
   * @returns the generated event envelopes.
   */
  generateSimulatedEvents(input: GenerateSimulatedEventsInput): DJSEMLiveEventEnvelope[] {
    // Deterministic: one "chapter_enter" event per chapter with a startTimeMs.
    const chapters = chaptersFrom(input.snapshot);
    return chapters.filter((c) => typeof c.startTimeMs === "number").map((c, i) => ({
      envelopeVersion: 1, eventId: `ev_${input.session.sessionId}_${i}`,
      eventKind: "chapter.enter", occurredAt: AT, payload: { chapterId: c.id, timeMs: c.startTimeMs },
    } as unknown as DJSEMLiveEventEnvelope));
  }

  /**
   * Bundles a rehearsal run's commands, events, and frames into a replay log.
   * @param input - the session plus its recorded commands, events, and frames.
   * @returns the assembled replay log (commands sorted deterministically by id).
   */
  exportReplay(input: ExportRehearsalReplayInput): DJSEMReplayLog {
    return {
      sessionId: input.session.sessionId,
      snapshotId: input.session.snapshotId,
      createdAt: AT,
      commands: [...input.commands].sort((a, b) => a.commandId.localeCompare(b.commandId)),
      events: [...input.events],
      frames: [...input.frames],
    };
  }

  /**
   * Validates structural invariants of a rehearsal session.
   * @param session - the session to validate.
   * @returns a validation result listing any structural issues.
   */
  validateSession(session: DJSEMRehearsalSession): DJSEMValidationResult {
    const issues: ValidationResult["issues"] = [];
    if (!session?.sessionId) issues.push({ path: "$.sessionId", message: "required" });
    if (!session?.snapshotId) issues.push({ path: "$.snapshotId", message: "required" });
    if (session?.currentTimeMs < 0) issues.push({ path: "$.currentTimeMs", message: "must be >= 0" });
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "rehearsal_session");
  }

  private chapterAt(snap: ApprovedSnapshotLike, timeMs: number): { id: string } | undefined {
    const chs = chaptersFrom(snap);
    return chs.find((c) => typeof c.startTimeMs === "number" && typeof c.endTimeMs === "number" && timeMs >= c.startTimeMs! && timeMs < c.endTimeMs!);
  }
}
